import { action, computed, observable } from 'mobx';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import CorrelationCollection from './CorrelationCollection';
import CorrelationStatisticsModel from './CorrelationStatisticsModel';
import {
  CORRELATIONS_PER_PAGE_DEFAULT,
  CORRELATION_PERMISSIONS,
  DEFAULT_CORRELATION_FILTERS,
  DEFAULT_CORRELATION_SORT
} from './constants';
import { activeRange } from './filters';
import { deriveGranularity, granularityChoices } from './granularity';
import type { CorrelationFilterValues, Granularity, TimeSelection } from './types';

type TimeRange = Pick<CorrelationFilterValues, 'lookback' | 'startDate' | 'endDate'>;

/** Drops unset values and coerces the dates the filter sidebar hands over as moments. */
function normalize(values: CorrelationFilterValues): CorrelationFilterValues {
  const next = omitBy(
    {
      ...values,
      startDate: values.startDate ? Number(values.startDate) : undefined,
      endDate: values.endDate ? Number(values.endDate) : undefined,
      createdFrom: values.createdFrom ? Number(values.createdFrom) : undefined,
      createdTo: values.createdTo ? Number(values.createdTo) : undefined
    },
    (value) => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)
  ) as CorrelationFilterValues;

  // The sidebar fills in dates alongside a lookback it restores from the URL; the lookback wins, so the window
  // keeps moving up to now.
  if (next.lookback) {
    delete next.startDate;
    delete next.endDate;
  }

  // A timeline created range is drawn in, and pins, the window it selects; it lapses once the window changes.
  if (next.createdFrom !== next.startDate || next.createdTo !== next.endDate) {
    delete next.createdFrom;
    delete next.createdTo;
  }

  return next;
}

export class CorrelationsStore {
  /** Set by `$store` for every store. */
  store?: { $auth: { hasRbacPermissions: (permissions: string[]) => boolean } };

  /** The correlations list's filters, as its filter sidebar edits them and the URL hash stores them. */
  @observable.ref
  filters: CorrelationFilterValues = DEFAULT_CORRELATION_FILTERS;

  /** Pinned when the filters change or the list refreshes, so the list, statistics, and timeline share it. */
  @observable.ref
  window: TimeSelection = activeRange(DEFAULT_CORRELATION_FILTERS);

  /** The timeline's chosen bucket size; unset picks the finest the window can carry. */
  @observable
  granularityValue: string | null = null;

  /** The time range from before the first timeline brush, restored when the brush is cleared. */
  private preBrushRange: TimeRange | null = null;

  collection = new CorrelationCollection([], {
    sortState: DEFAULT_CORRELATION_SORT as CorrelationCollection['sortState']
  });

  statistics = new CorrelationStatisticsModel({ id: 'filtered' });

  /** Counts over the time scope only, so ticking one facet never zeroes out the others. */
  facetStatistics = new CorrelationStatisticsModel({ id: 'facets' });

  constructor() {
    this.collection.setPageState({ perPage: CORRELATIONS_PER_PAGE_DEFAULT });
  }

  /** Can ack and un-ack correlations. */
  @computed
  get canAck(): boolean {
    return Boolean(this.store?.$auth.hasRbacPermissions([CORRELATION_PERMISSIONS.ack]));
  }

  /** Can clear, merge, and remove alerts from correlations. */
  @computed
  get canClear(): boolean {
    return Boolean(this.store?.$auth.hasRbacPermissions([CORRELATION_PERMISSIONS.clear]));
  }

  @computed
  get granularity(): Granularity {
    const spanMs = this.window.end - this.window.start;
    return (
      granularityChoices(spanMs).find((entry) => entry.value === this.granularityValue) ?? deriveGranularity(spanMs)
    );
  }

  /** The filters with their time range pinned to `window`. */
  @computed
  get query(): CorrelationFilterValues {
    return { ...this.filters, lookback: undefined, startDate: this.window.start, endDate: this.window.end };
  }

  /** The range selected on the timeline, if any. */
  @computed
  get createdRange(): TimeSelection | null {
    const { createdFrom, createdTo } = this.filters;
    return createdFrom && createdTo ? { start: createdFrom, end: createdTo } : null;
  }

  /** The list page's first load, from the filters it restored from the URL (or the defaults). */
  @action
  loadList(values: CorrelationFilterValues): Promise<unknown> {
    this.filters = normalize(values);
    this.window = activeRange(this.filters);
    return this.load();
  }

  /** Applies the filter sidebar's values and reloads, unless nothing changed. */
  @action
  setFilters(values: CorrelationFilterValues): Promise<unknown> {
    const next = normalize(values);
    if (isEqual(next, this.filters)) {
      return Promise.resolve();
    }
    this.filters = next;
    this.window = activeRange(next);
    return this.load();
  }

  /** Reloads the list and its statistics, with a lookback window moved up to now. */
  @action
  refresh(): Promise<unknown> {
    this.window = activeRange(this.filters);
    return this.load({ refresh: true });
  }

  /** Fetches the page the paginator has moved the collection to. */
  fetchPage({ refetchTotals = false }: { refetchTotals?: boolean } = {}): Promise<unknown> {
    if (refetchTotals) {
      this.applyTotals();
    }
    return this.collection.fetch().catch(() => null);
  }

  @action
  setGranularity(value: string): Promise<unknown> {
    this.granularityValue = value;
    return this.loadStatistics();
  }

  /** Reloads the statistics after correlations change underneath them (acked, cleared, merged). */
  loadStatistics(): Promise<unknown> {
    const { granularity, window, filters } = this;
    const { createdFrom, createdTo } = filters;

    return Promise.all([
      this.statistics.fetch({ filter: this.query, granularity }).then(() => this.applyTotals()),
      this.facetStatistics.fetch({
        filter: { startDate: window.start, endDate: window.end, createdFrom, createdTo },
        granularity
      })
    ]).catch(() => null);
  }

  /**
   * The filters a timeline brush selects: the brushed range as both the window and the created range. Clearing
   * the brush restores the time range from before the first brush.
   */
  brushedFilters(range: TimeSelection | null): CorrelationFilterValues {
    const { filters } = this;

    if (range) {
      this.preBrushRange ??= { lookback: filters.lookback, startDate: filters.startDate, endDate: filters.endDate };
      return {
        ...filters,
        lookback: undefined,
        startDate: range.start,
        endDate: range.end,
        createdFrom: range.start,
        createdTo: range.end
      };
    }

    const { lookback, startDate, endDate } = this.preBrushRange ?? DEFAULT_CORRELATION_FILTERS;
    this.preBrushRange = null;
    return { ...filters, lookback, startDate, endDate, createdFrom: undefined, createdTo: undefined };
  }

  @action
  resetList(): void {
    this.filters = DEFAULT_CORRELATION_FILTERS;
    this.window = activeRange(DEFAULT_CORRELATION_FILTERS);
    this.granularityValue = null;
    this.preBrushRange = null;
    this.collection.groupBy = null;
    this.collection.sortState = { ...DEFAULT_CORRELATION_SORT } as CorrelationCollection['sortState'];
    this.collection.clearSelection();
    this.collection.set([]);
    this.collection.lastFetched = null;
    this.collection.resetPageState();
  }

  // Here and in `loadStatistics`/`fetchPage`, failures are kept on the collection and statistics models, which the
  // page renders, so the promises are settled rather than left to reject unhandled.
  private load({ refresh = false }: { refresh?: boolean } = {}): Promise<unknown> {
    return Promise.all([
      this.collection.setServerFilter(this.query, { refresh }).catch(() => null),
      this.loadStatistics()
    ]);
  }

  /** The statistics count the filtered correlations, which pages the list (as `$alerting.handleTotalCountLoad`). */
  @action
  private applyTotals(): void {
    const { totalCount } = this.statistics.summary;
    const { perPage } = this.collection.pageState;
    this.collection.setPageState({
      totalCount,
      pageCount: Math.max(1, Math.ceil(totalCount / perPage)),
      pageCountLoaded: true
    });
  }
}

export default new CorrelationsStore();
